import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, Chip, Typography } from '@mui/material';
import { CreditCard, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ThirdPartyUsageCard } from '../components/dashboard/ThirdPartyUsageCard';
import { TokenUsageChart } from '../components/dashboard/TokenUsageChart';
import { UsageQuotaCard } from '../components/dashboard/UsageQuotaCard';
import { useAgents } from '../contexts/AgentsContext';
import { useAuth } from '../contexts/AuthContext';
import { getQuota, getSubscription, getTokenUsageSeries } from '../services/quota.service';
import { getThirdPartyUsage } from '../services/third-party-usage.service';
import type {
  SubscriptionInfo,
  ThirdPartyUsageRow,
  TokenUsagePeriod,
  TokenUsagePoint,
  UserQuota,
} from '../types';

const THIRD_PARTY_USAGE_LIMIT = 5;
const EMPTY_THIRD_PARTY_TOTAL = { messages: 0, tokens: 0, credits: 0, toolCalls: 0 };

export const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user, quota, subscription } = useAuth();
  const { agents } = useAgents();
  const displayName = user?.fullName || user?.userName || t('dashboard.userFallback');
  const [usagePeriod, setUsagePeriod] = useState<TokenUsagePeriod>('day');
  const [usagePoints, setUsagePoints] = useState<TokenUsagePoint[]>([]);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [thirdPartyByTenant, setThirdPartyByTenant] = useState<ThirdPartyUsageRow[]>([]);
  const [thirdPartyByAgent, setThirdPartyByAgent] = useState<ThirdPartyUsageRow[]>([]);
  const [thirdPartyTotal, setThirdPartyTotal] = useState(EMPTY_THIRD_PARTY_TOTAL);
  const [thirdPartyLoading, setThirdPartyLoading] = useState(false);
  const [thirdPartyError, setThirdPartyError] = useState<string | null>(null);
  const [dashboardQuota, setDashboardQuota] = useState<UserQuota | null>(
    quota ?? user?.quota ?? null
  );
  const [dashboardSubscription, setDashboardSubscription] = useState<SubscriptionInfo | null>(
    subscription ?? user?.subscription ?? null
  );
  const [dashboardSummaryLoading, setDashboardSummaryLoading] = useState(true);
  const userAgents = useMemo(
    () => agents.filter((agent) => agent.ownerType !== 'SYSTEM'),
    [agents]
  );
  const currentQuota = dashboardQuota ?? quota ?? user?.quota ?? null;
  const subscriptionCode = dashboardSubscription?.packageCode.toLowerCase();
  const subscriptionPlanName =
    dashboardSubscription?.packageName || t('dashboard.subscription.free');

  useEffect(() => {
    setDashboardQuota(quota ?? user?.quota ?? null);
  }, [quota, user?.quota]);

  useEffect(() => {
    setDashboardSubscription(subscription ?? user?.subscription ?? null);
  }, [subscription, user?.subscription]);

  useEffect(() => {
    let isActive = true;

    const loadDashboardSummary = async () => {
      setDashboardSummaryLoading(true);
      try {
        const [quotaData, subscriptionData] = await Promise.allSettled([
          getQuota(),
          getSubscription(),
        ]);
        if (!isActive) return;
        if (quotaData.status === 'fulfilled') setDashboardQuota(quotaData.value);
        if (subscriptionData.status === 'fulfilled') {
          setDashboardSubscription({
            status: subscriptionData.value.status,
            packageName: subscriptionData.value.package.name,
            packageCode: subscriptionData.value.package.code,
            isTrialing: subscriptionData.value.isTrialSubscription,
            trialDaysRemaining: subscriptionData.value.trialEndsAt
              ? Math.ceil(
                  (new Date(subscriptionData.value.trialEndsAt).getTime() - Date.now()) /
                    (1000 * 60 * 60 * 24)
                )
              : null,
            expiresAt: subscriptionData.value.endAt,
          });
        }
      } finally {
        if (isActive) setDashboardSummaryLoading(false);
      }
    };

    void loadDashboardSummary();
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    const loadUsage = async () => {
      setUsageLoading(true);
      setUsageError(null);
      try {
        const data = await getTokenUsageSeries(usagePeriod);
        if (isActive) setUsagePoints(data.points);
      } catch (error) {
        if (isActive) {
          setUsagePoints([]);
          setUsageError(
            error instanceof Error ? error.message : t('dashboard.tokenUsage.errors.loadFailed')
          );
        }
      } finally {
        if (isActive) setUsageLoading(false);
      }
    };

    void loadUsage();
    return () => {
      isActive = false;
    };
  }, [t, usagePeriod]);

  useEffect(() => {
    let isActive = true;
    const loadThirdPartyUsage = async () => {
      setThirdPartyLoading(true);
      setThirdPartyError(null);
      try {
        const dateRange = getUsageDateRange();
        const [tenantUsage, agentUsage] = await Promise.all([
          getThirdPartyUsage({ ...dateRange, groupBy: 'external_tenant' }),
          getThirdPartyUsage({ ...dateRange, groupBy: 'agent' }),
        ]);
        if (!isActive) return;
        setThirdPartyTotal(
          tenantUsage.data.reduce(
            (total, row) => ({
              messages: total.messages + row.messages,
              tokens: total.tokens + row.total_tokens,
              credits: total.credits + row.cost_credits,
              toolCalls: total.toolCalls + row.tool_calls,
            }),
            EMPTY_THIRD_PARTY_TOTAL
          )
        );
        setThirdPartyByTenant(tenantUsage.data.slice(0, THIRD_PARTY_USAGE_LIMIT));
        setThirdPartyByAgent(agentUsage.data.slice(0, THIRD_PARTY_USAGE_LIMIT));
      } catch (error) {
        if (!isActive) return;
        setThirdPartyByTenant([]);
        setThirdPartyByAgent([]);
        setThirdPartyTotal(EMPTY_THIRD_PARTY_TOTAL);
        setThirdPartyError(
          error instanceof Error ? error.message : t('dashboard.thirdPartyUsage.errors.loadFailed')
        );
      } finally {
        if (isActive) setThirdPartyLoading(false);
      }
    };

    void loadThirdPartyUsage();
    return () => {
      isActive = false;
    };
  }, [t]);

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <Box className="mb-6 md:mb-8">
        <Typography variant="h4" className="font-bold text-gray-900 dark:text-white">
          {t('dashboard.welcome', { name: displayName })}
        </Typography>
        <Typography variant="body2" className="mt-1 text-gray-500 dark:text-slate-400">
          {t('dashboard.subtitle')}
        </Typography>
      </Box>

      <Box className="mb-6 grid grid-cols-1 gap-4 md:mb-8 md:grid-cols-2 md:gap-6 xl:grid-cols-3">
        <StatCard
          title={t('dashboard.stats.totalAgents')}
          value={userAgents.length.toString()}
          caption={t('dashboard.stats.agentsCreated')}
          icon={<Users className="text-blue-600 dark:text-blue-400" size={24} />}
        />
        <SubscriptionCard
          packageName={subscriptionPlanName}
          packageCode={subscriptionCode}
          description={
            subscriptionCode === 'pro'
              ? t('dashboard.subscription.proDescription')
              : subscriptionCode === 'enterprise'
                ? t('dashboard.subscription.enterpriseDescription')
                : t('dashboard.subscription.freeDescription')
          }
        />
        <UsageQuotaCard quota={currentQuota} loading={dashboardSummaryLoading} />
      </Box>

      <TokenUsageChart
        period={usagePeriod}
        points={usagePoints}
        loading={usageLoading}
        error={usageError}
        onPeriodChange={setUsagePeriod}
      />
      <ThirdPartyUsageCard
        tenantRows={thirdPartyByTenant}
        agentRows={thirdPartyByAgent}
        total={thirdPartyTotal}
        loading={thirdPartyLoading}
        error={thirdPartyError}
      />
      <RecentActivityCard userAgents={userAgents} />
    </div>
  );
};

const StatCard: React.FC<{
  title: string;
  value: string;
  caption: string;
  icon: React.ReactNode;
}> = ({ title, value, caption, icon }) => (
  <Card
    className="border border-gray-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800"
    elevation={0}
  >
    <Box className="flex items-start justify-between">
      <Box>
        <Typography variant="body2" className="mb-1 font-medium text-gray-500 dark:text-slate-400">
          {title}
        </Typography>
        <Typography variant="h3" className="font-bold text-gray-900 dark:text-white">
          {value}
        </Typography>
        <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
          {caption}
        </Typography>
      </Box>
      <Box className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
        {icon}
      </Box>
    </Box>
  </Card>
);

const SubscriptionCard: React.FC<{
  packageName: string;
  packageCode?: string;
  description: string;
}> = ({ packageName, packageCode, description }) => {
  const { t } = useTranslation();

  return (
    <Card
      className="border border-gray-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      elevation={0}
    >
      <Box className="flex items-start justify-between">
        <Box className="flex-1">
          <Typography
            variant="body2"
            className="mb-1 font-medium text-gray-500 dark:text-slate-400"
          >
            {t('dashboard.stats.subscriptionPlan')}
          </Typography>
          <Box className="mt-2">
            <Chip
              label={t('dashboard.subscription.planLabel', { name: packageName })}
              className={`font-semibold ${
                packageCode === 'enterprise'
                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                  : packageCode === 'pro'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300'
              }`}
              size="medium"
            />
          </Box>
          <Typography variant="caption" className="mt-2 block text-gray-500 dark:text-slate-400">
            {description}
          </Typography>
        </Box>
        <Box className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
          <CreditCard className="text-blue-600 dark:text-blue-400" size={24} />
        </Box>
      </Box>
    </Card>
  );
};

const RecentActivityCard: React.FC<{
  userAgents: Array<{
    id: string;
    name: string;
    description?: string;
    knowledgeCount?: number;
    isDefault?: boolean;
  }>;
}> = ({ userAgents }) => {
  const { t } = useTranslation();

  return (
    <Card
      className="border border-gray-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800"
      elevation={0}
    >
      <Typography variant="h6" className="mb-4 font-semibold text-gray-900 dark:text-white">
        {t('dashboard.recentActivity.title')}
      </Typography>
      <Box className="space-y-3">
        {userAgents.slice(0, 5).map((agent) => (
          <Box
            key={agent.id}
            className="flex items-center gap-3 rounded-lg border border-gray-100 p-3 dark:border-slate-700"
          >
            <Box className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
              <Users className="text-blue-600 dark:text-blue-400" size={16} />
            </Box>
            <Box className="flex-1">
              <Typography variant="body2" className="font-medium text-gray-900 dark:text-white">
                {agent.name}
              </Typography>
              <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
                {agent.description ||
                  t('dashboard.recentActivity.knowledgeSources', {
                    count: agent.knowledgeCount ?? 0,
                  })}
              </Typography>
            </Box>
            {agent.isDefault && (
              <Chip
                label={t('common.default')}
                size="small"
                className="bg-blue-100 text-blue-700"
              />
            )}
          </Box>
        ))}
        {userAgents.length === 0 && (
          <Box className="py-8 text-center">
            <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
              {t('dashboard.recentActivity.empty')}
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

function getUsageDateRange() {
  const to = new Date();
  const from = new Date(to);
  from.setDate(from.getDate() - 30);
  return { from: from.toISOString(), to: to.toISOString() };
}
